import { Roles } from 'src/common/types/role.enum'

export enum NotificationType {
  Message = 'message',
  MessageUrgent = 'messageUrgent',
  Journal = 'journal',
  Medication = 'medication',
  IncidentReport = 'incidentReport',
  Financial = 'financial',
  Approval = 'approval',
  ExpiringCredentials = 'expiringCredentials',
  Onboarding = 'onboarding',
  Learning = 'learning',
  Calendar = 'calendar',
  Rsvp = 'rsvp',
  Email = 'email',
}

export interface INotificationPreference {
  notificationType: NotificationType
  enabled: boolean
  centerIds?: string[] // Only for in-app notifications
}

export interface INotificationPreferences {
  preferences: INotificationPreference[]
  userId?: string
  updatedUtc?: string
}

export interface IPushNotificationPreference {
  notificationType: NotificationType
  enabled: boolean
}

export interface IPushNotificationPreferences {
  preferences: IPushNotificationPreference[]
  disabledCenters?: string[]
  userId?: string
  updatedUtc?: string
}

export interface ICenterOption {
  id: string
  name: string
}

export interface NotificationTypeConfig {
  type: NotificationType
  name: string
  description: string
  icon: string
  testEnvOnly?: boolean
  featureFlag?: string
  pushOnly?: boolean
  inAppOnly?: boolean
  excludedRoles?: string[]
}

// Notification Type Configurations for UI - Admin Portal
export const ADMIN_NOTIFICATION_TYPE_CONFIGS: NotificationTypeConfig[] = [
  {
    type: NotificationType.Message,
    name: 'Messages',
    description: 'New messages from family members',
    icon: 'message',
    pushOnly: true,
  },
  {
    type: NotificationType.MessageUrgent,
    name: 'Urgent Messages',
    description: 'High-priority messages from other staff that require acknowledgment',
    icon: 'priority_high',
    pushOnly: true,
  },
  {
    type: NotificationType.IncidentReport,
    name: 'Incident & Illness Reports',
    description: 'Reports awaiting approval and approval rejections',
    icon: 'local_hospital',
  },
  {
    type: NotificationType.Medication,
    name: 'Medications',
    description: 'Medication due reminders and past due alerts',
    icon: 'medication',
  },
  {
    type: NotificationType.Financial,
    name: 'Financials',
    description: 'Invoices, payments, chargebacks, and billing updates',
    icon: 'payments',
    excludedRoles: [Roles.RoomStaff],
  },
  {
    type: NotificationType.Approval,
    name: 'Approvals',
    description: 'Info changes and items awaiting your approval',
    icon: 'fact_check',
    excludedRoles: [Roles.RoomStaff],
  },
  {
    type: NotificationType.ExpiringCredentials,
    name: 'Expiring Credentials',
    description:
      'Alerts for sponsors, criminal checks, and first aid credentials expiring in 30, 14, 7, or 1 day (sent at 11:00 AM)',
    icon: 'badge',
    excludedRoles: [Roles.RoomStaff],
  },
  {
    type: NotificationType.Onboarding,
    name: 'Onboarding',
    description: 'New public registrations and enrollment requests',
    icon: 'person_add',
    excludedRoles: [Roles.RoomStaff],
  },
  {
    type: NotificationType.Rsvp,
    name: 'RSVP',
    description: 'Student RSVP notifications',
    icon: 'event_busy',
    excludedRoles: [Roles.RoomStaff],
  },
  {
    type: NotificationType.Learning,
    name: 'Learning',
    description: 'Learning plans and progress report updates',
    icon: 'school',
    featureFlag: 'learning',
  },
  {
    type: NotificationType.Calendar,
    name: 'Calendar',
    description: 'Calendar event reminders and updates',
    icon: 'event',
    featureFlag: 'calendar',
  },
]

// Notification Type Configurations for Client Portal
export const CLIENT_NOTIFICATION_TYPE_CONFIGS: NotificationTypeConfig[] = [
  {
    type: NotificationType.Message,
    name: 'Messages',
    description: "New messages from your center's staff",
    icon: 'message',
    pushOnly: true,
  },
  {
    type: NotificationType.Journal,
    name: 'Journals',
    description: 'Daily journal updates and activity reports for your child',
    icon: 'book',
  },
  {
    type: NotificationType.IncidentReport,
    name: 'Incident and Health Reports',
    description: 'Incident and health reports about your child',
    icon: 'local_hospital',
  },
  {
    type: NotificationType.Financial,
    name: 'Financials',
    description: 'Invoice and billing updates and payments made for you',
    icon: 'payments',
  },
  {
    type: NotificationType.Learning,
    name: 'Learning',
    description: 'Learning milestones and developmental progress updates',
    icon: 'school',
    featureFlag: 'learning',
  },
  {
    type: NotificationType.Calendar,
    name: 'Calendar',
    description: 'Event reminders, closures, and schedule changes',
    icon: 'event',
    featureFlag: 'calendar',
  },
  {
    type: NotificationType.Email,
    name: 'Email',
    description: 'Notifications when your center sends you an email',
    icon: 'mail',
  },
]

// Backward compatibility - defaults to admin configs
export const NOTIFICATION_TYPE_CONFIGS = ADMIN_NOTIFICATION_TYPE_CONFIGS
